<template>
  <div class="patient-exercises-report--wrap">
    <div class="patient-exercises-report">
      <v-container>
        <v-row>
          <v-col class="d-flex">
            <v-spacer />
            <date-range-calendars-picker
              :key="initialEndDate + '-' + initialStartDate"
              :mode="'select'"
              :initialEndDate="selectedRange.endDate"
              :initialStartDate="selectedRange.startDate"
              @select-range="prepareLoadExercisesReport"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h2 class="mb-4">
              {{
                tenantTexts.participantRoutinesProgress
                  .sectionExercisesActivities.title
              }}
            </h2>
            <v-btn-toggle
              v-model="modeType"
              borderless
              color="accent"
              mandatory
              small
            >
              <v-btn value="all">All ({{ exercises.all.allCount }})</v-btn>
              <v-btn value="exercises"
                >Exercises ({{ exercises.exercises.allCount }})</v-btn
              >
              <v-btn value="activitiesAM"
                >{{
                  tenantTexts.participantRoutinesProgress
                    .sectionExercisesActivities.AM_Activities
                }}
                ({{ exercises.activitiesAM.allCount }})</v-btn
              >
              <v-btn value="activitiesPM"
                >{{
                  tenantTexts.participantRoutinesProgress
                    .sectionExercisesActivities.PM_Activities
                }}
                ({{ exercises.activitiesPM.allCount }})</v-btn
              >
            </v-btn-toggle>
            <div class="d-flex patient-exercises-report__filters--cards">
              <v-card
                elevation="0"
                :class="modeStatus == 'all' ? 'active' : ''"
                @click="modeStatus = 'all'"
              >
                <h3>
                  <animated-number :number="exercises[modeType].allCount" />
                  <v-icon size="126" color="accent">$chart</v-icon>
                </h3>
                <p>All</p>
              </v-card>
              <v-card
                v-if="exercises[modeType].superCompletedCount > 0"
                elevation="0"
                class="super-completed"
                :class="modeStatus == 'superCompleted' ? 'active' : ''"
                @click="modeStatus = 'superCompleted'"
              >
                <h3>
                  <animated-number
                    :number="exercises[modeType].superCompletedCount"
                  />
                  <v-icon size="126">$chart</v-icon>
                </h3>
                <p><span class="dot"></span>Super Completed</p>
              </v-card>
              <v-card
                elevation="0"
                class="completed"
                :class="modeStatus == 'completed' ? 'active' : ''"
                @click="modeStatus = 'completed'"
              >
                <h3>
                  <animated-number
                    :number="exercises[modeType].completedCount"
                  />
                  <v-icon size="126">$chart</v-icon>
                </h3>
                <p><span class="dot"></span>Completed</p>
              </v-card>
              <v-card
                v-if="exercises[modeType].partiallyCount > 0"
                elevation="0"
                class="partially"
                :class="modeStatus == 'partially' ? 'active' : ''"
                @click="modeStatus = 'partially'"
              >
                <h3>
                  <animated-number
                    :number="exercises[modeType].partiallyCount"
                  />
                  <v-icon size="126">$chart</v-icon>
                </h3>
                <p><span class="dot"></span>Attempted</p>
              </v-card>
              <v-card
                elevation="0"
                class="skipped"
                :class="modeStatus == 'skipped' ? 'active' : ''"
                @click="modeStatus = 'skipped'"
              >
                <h3>
                  <animated-number :number="exercises[modeType].skippedCount" />
                  <v-icon size="126">$chart</v-icon>
                </h3>
                <p><span class="dot"></span>Skipped</p>
              </v-card>
            </div>
            <horizontal-states-bar
              :super-completed="exercises[modeType].superCompletedCount"
              :completed="exercises[modeType].completedCount"
              :skipped="exercises[modeType].skippedCount"
              :partially="exercises[modeType].partiallyCount"
            />
            <bar-chart
              v-if="
                computedExercisesReport && computedExercisesReport.exercises
              "
              :chart-data="exercises[modeType]"
              :chart-options="barChartOptions"
            />
          </v-col>
        </v-row>
        <v-expansion-panels class="metrics-expansion-panel">
          <v-expansion-panel>
            <v-expansion-panel-header
              ><v-row>
                <v-col>
                  <h2>Metrics</h2>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="6" class="patient-exercises-report__metrics">
                  <h5>Time spent training</h5>
                  <h3><animated-number :number="time.allCount" /></h3>
                  <p v-if="exercisesReportTotals">
                    {{ exercisesReportTotals.time }} from <strong>Day 1</strong>
                  </p>
                  <line-chart
                    v-if="computedExercisesReport && time"
                    :chart-data="time"
                    :chart-options="lineChartOptions"
                  />
                </v-col>
                <v-col cols="6" class="patient-exercises-report__metrics">
                  <h5>Pounds lifted</h5>
                  <h3><animated-number :number="weight.allCount" /></h3>
                  <p v-if="exercisesReportTotals">
                    {{ exercisesReportTotals.weight }} from
                    <strong>Day 1</strong>
                  </p>
                  <line-chart
                    v-if="computedExercisesReport && weight"
                    :chart-data="weight"
                    :chart-options="lineChartOptions"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" class="patient-exercises-report__metrics">
                  <h5>Distance moved</h5>
                  <h3><animated-number :number="distanceRun.allCount" /></h3>
                  <p v-if="exercisesReportTotals">
                    {{ exercisesReportTotals.distance }} from
                    <strong>Day 1</strong>
                  </p>
                  <line-chart
                    v-if="computedExercisesReport && distanceRun"
                    :chart-data="distanceRun"
                    :chart-options="lineChartOptions"
                  />
                </v-col>
                <v-col cols="6" class="patient-exercises-report__metrics">
                  <h5>Steps</h5>
                  <h3><animated-number :number="steps.allCount" /></h3>
                  <p v-if="exercisesReportTotals">
                    {{ exercisesReportTotals.steps }} from
                    <strong>Day 1</strong>
                  </p>
                  <line-chart
                    v-if="
                      computedExercisesReport && computedExercisesReport.steps
                    "
                    :chart-data="steps"
                    :chart-options="lineChartOptions"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row v-if="computedExercisesReport.days.length > 0">
          <v-col>
            <h2 class="mb-4 d-inline-block mr-4">Daily performance</h2>
            <v-switch
              class="d-inline-block mt-0"
              :ripple="false"
              small
              label="expand all"
              color="accent"
              v-model="stateDailyRows"
              @click="expandAll"
            ></v-switch>
            <daily-performance-row
              v-for="day in computedExercisesReport.days"
              :key="day.date"
              :day="day"
              :data="findDayData(day.date)"
              :day-report="computedDayReport[day.date]"
              :state-open="stateDailyRows"
              @load-day-report="loadDayReport"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import DateRangeCalendarsPicker from '@/components/inputs/DateRangeCalendarsPicker.vue'
import BarChart from '@/components/exercises/BarChart.vue'
import LineChart from '@/components/exercises/LineChart.vue'
import AnimatedNumber from '@/components/exercises/AnimatedNumber.vue'
import HorizontalStatesBar from '@/components/exercises/HorizontalStatesBar.vue'
import DailyPerformanceRow from '@/components/exercises/DailyPerformanceRow.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'

import { debounce } from '@/helpers/debounce'

// @ is an alias to /src
export default {
  name: 'PatientExercisesReport',
  components: {
    DateRangeCalendarsPicker,
    BarChart,
    LineChart,
    AnimatedNumber,
    HorizontalStatesBar,
    DailyPerformanceRow,
  },
  mixins: [TenantTextsMixin],
  data: () => ({
    initialEndDate: '',
    initialStartDate: '',
    selectedRange: {},
    modeType: 'all',
    modeStatus: 'all',
    stateDailyRows: false,
    barChartOptions: {
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          display: true,
          title: {
            display: true,
          },
        },
        y: {
          stacked: true,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          reverse: true,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''

              if (label) {
                label += ': '
              }
              if (context.parsed.y > 0) {
                label += context.parsed.y
                return label
              } else {
                return null
              }
            },
          },
        },
      },
      maintainAspectRatio: false,
    },
    lineChartOptions: {
      interaction: {
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
          },
        },
      },
      maintainAspectRatio: false,
    },
  }),
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'exercisesReport',
      'exercisesReportTotals',
      'exercisesDayReport',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFileType',
    ]),
    profileId() {
      return this.currentPatient.profileId
    },
    isRoleAllowed() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] !==
        this.dictionaryBhUsersRoleIds.Consultant
      )
    },
    monthsStart() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('month')
        .format('YYYY-MM-DD')
    },
    monthsEnd() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('month')
        .format('YYYY-MM-DD')
    },
    weeksStart() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .format('YYYY-MM-DD')
    },
    weeksStartMonthName() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .format('MMMM')
    },
    weeksEnd() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .format('YYYY-MM-DD')
    },
    weeksEndMonthName() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .format('MMMM')
    },
    weekDays() {
      let days = []
      var day = this.$moment(this.weeksStart)
      while (day <= this.$moment(this.weeksEnd)) {
        days.push(day.toDate())
        day = day.clone().add(1, 'd')
      }
      return days
    },
    today() {
      return new Date()
    },
    lastWeek() {
      return this.$moment(this.today)
        .subtract(6, 'days')
        .toDate()
    },
    lastMonth() {
      return this.$moment(this.today)
        .subtract(30, 'days')
        .toDate()
    },
    computedExercisesReport() {
      let startDate = this.$moment(this.selectedRange.startDate)
      let endDate = this.$moment(this.selectedRange.endDate)

      let dateList = this.getDaysBetweenDates(startDate, endDate)

      let days = []

      let report = {
        exercises: [],
        activitiesAM: [],
        activitiesPM: [],
        steps: [],
      }
      if (this.exercisesReport) {
        dateList.forEach(date => {
          Object.keys(report).forEach(key => {
            if (this.exercisesReport[key]) {
              let ind = this.exercisesReport[key].findIndex(
                item => item.date == date,
              )
              if (ind > -1) {
                report[key].push({ ...this.exercisesReport[key][ind] })
              } else {
                let tmpObj = {
                  date: date,
                  completed: 0,
                  superCompleted: 0,
                  partially: 0,
                  skipped: 0,
                  time: NaN,
                  progressTime: NaN,
                  weight: NaN,
                  progressWeight: NaN,
                  distance: NaN,
                  progressDistance: NaN,
                  steps: NaN,
                  artificial: true,
                }
                report[key].push({ ...tmpObj })
              }
            }
          })
        })
        report.days = []
        dateList.forEach(date => {
          let tmpObj = {
            date: date,
          }
          let day = this.exercisesReport.days.find(day => day.date == date)
          if (day) {
            tmpObj = { ...day }
          }
          report.days.push(tmpObj)
        })
      } else {
        report.days = []
      }

      report.days = report.days.sort((a, b) => {
        if (b?.date < a?.date) {
          return -1
        }
        if (b?.date > a?.date) {
          return 1
        }
        return 0
      })

      return report
    },
    exercises() {
      let modes = ['all', 'exercises', 'activitiesAM', 'activitiesPM']
      let ret = {}
      modes.forEach(mode => {
        let datasets = []
        let completed = []
        let superCompleted = []
        let skipped = []
        let partially = []
        let labels = []
        let allCount = 0
        let superCompletedCount = 0
        let completedCount = 0
        let partiallyCount = 0
        let skippedCount = 0
        let computedTypes = []
        let items = []
        let summedTypes = []
        if (this.computedExercisesReport && this.exercisesReport) {
          items = [...this.computedExercisesReport.exercises]
          if (mode == 'all') {
            summedTypes = [
              ...this.computedExercisesReport.exercises,
              ...this.computedExercisesReport.activitiesAM,
              ...this.computedExercisesReport.activitiesPM,
            ]
            summedTypes.forEach(item => {
              let ind = computedTypes.findIndex(t => t.date == item.date)
              if (ind > -1) {
                if (item.superCompleted)
                  computedTypes[ind]['superCompleted'] += item.superCompleted
                if (item.completed)
                  computedTypes[ind]['completed'] += item.completed
                if (item.partially)
                  computedTypes[ind]['partially'] += item.partially
                if (item.skipped) computedTypes[ind]['skipped'] += item.skipped
              } else {
                computedTypes.push({ ...item })
              }
            })
          }
          switch (mode) {
            case 'all':
              items = [...computedTypes]
              break
            case 'activitiesAM':
              items = [...this.computedExercisesReport.activitiesAM]
              break
            case 'activitiesPM':
              items = [...this.computedExercisesReport.activitiesPM]
              break
            case 'exercises':
              items = [...this.computedExercisesReport.exercises]
              break
          }

          if (items)
            items.forEach(item => {
              superCompleted.push(item.superCompleted ? item.superCompleted : 0)
              completed.push(item.completed ? item.completed : 0)
              partially.push(item.partially ? item.partially : 0)
              skipped.push(item.skipped ? item.skipped : 0)
              allCount +=
                (item.superCompleted ? item.superCompleted : 0) +
                (item.completed ? item.completed : 0) +
                (item.skipped ? item.skipped : 0) +
                (item.partially ? item.partially : 0)
              superCompletedCount += item.superCompleted
                ? item.superCompleted
                : 0
              completedCount += item.completed ? item.completed : 0
              partiallyCount += item.partially ? item.partially : 0
              skippedCount += item.skipped ? item.skipped : 0
              if (!labels[this.$moment(item.date).format('MM/DD/YYYY')])
                labels.push(this.$moment(item.date).format('MM/DD/YYYY'))
            })
          if (this.modeStatus == 'all' || this.modeStatus == 'skipped')
            datasets.push({
              label: 'Skipped',
              data: skipped,
              backgroundColor: '#FF6760',
              barThickness: 8,
              borderRadius: 999,
              borderColor: '#FF6760',
              borderWidth: 2,
              borderSkipped: true,
            })
          if (this.modeStatus == 'all' || this.modeStatus == 'partially')
            datasets.push({
              label: 'Attempted',
              data: partially,
              backgroundColor: '#BDD0FF',
              barThickness: 8,
              borderRadius: 999,
              borderColor: '#BDD0FF',
              borderWidth: 2,
              borderSkipped: true,
            })
          if (this.modeStatus == 'all' || this.modeStatus == 'completed')
            datasets.push({
              label: 'Completed',
              data: completed,
              backgroundColor: '#00A0E0',
              barThickness: 8,
              borderRadius: 999,
              borderColor: '#00A0E0',
              borderWidth: 2,
              borderSkipped: true,
            })
          if (this.modeStatus == 'all' || this.modeStatus == 'superCompleted')
            datasets.push({
              label: 'Super Completed',
              data: superCompleted,
              backgroundColor: '#945EFF',
              barThickness: 8,
              borderRadius: 999,
              borderColor: '#945EFF',
              borderWidth: 2,
              borderSkipped: true,
            })
        }
        let tmpObj2 = {
          labels: labels,
          datasets: datasets,
          allCount: allCount,
          superCompletedCount: superCompletedCount,
          completedCount: completedCount,
          partiallyCount: partiallyCount,
          skippedCount: skippedCount,
        }
        ret[mode] = { ...tmpObj2 }
      })
      return ret
    },
    steps() {
      let labels = []
      let datasets = []
      let items = []
      let steps = []
      let allCount = 0
      if (this.computedExercisesReport) {
        items = [...this.computedExercisesReport.steps]
        if (items)
          items.forEach(item => {
            if (!labels[this.$moment(item.date).format('MM/DD/YYYY')])
              labels.push(this.$moment(item.date).format('MM/DD/YYYY'))
            steps.push(item.steps)
            allCount += item.steps ? item.steps : 0
          })
        datasets.push({
          label: 'Steps',
          data: steps,
          fill: true,
          borderColor: '#945EFF',
          backgroundColor: ctx => {
            const canvas = ctx.chart.ctx
            const gradient = canvas.createLinearGradient(0, 0, 0, 160)
            gradient.addColorStop(0, 'rgba(148, 94, 255, 0.2)')
            gradient.addColorStop(1, 'rgba(148, 94, 255, 0)')

            return gradient
          },
          tension: 0.2,
        })
      }
      return {
        labels: labels,
        datasets: datasets,
        allCount: allCount,
      }
    },
    distanceRun() {
      let labels = []
      let dataset = []
      let allCount = 0
      let items = []
      if (this.computedExercisesReport) {
        items = [...this.computedExercisesReport.exercises]
        if (items)
          items.forEach(item => {
            labels.push(this.$moment(item.date).format('MM/DD/YYYY'))
            dataset.push(item.progressDistance)
            allCount += item.progressDistance ? item.progressDistance : 0
          })
      }
      return {
        labels: labels,
        datasets: [
          {
            label: 'Distance run',
            data: dataset,
            fill: true,
            borderColor: '#55CB23',
            backgroundColor: ctx => {
              const canvas = ctx.chart.ctx
              const gradient = canvas.createLinearGradient(0, 0, 0, 160)
              gradient.addColorStop(0, 'rgba(85, 203, 35, 0.2)')
              gradient.addColorStop(1, 'rgba(85, 203, 35, 0)')

              return gradient
            },
            tension: 0.2,
          },
        ],
        allCount: allCount,
      }
    },
    time() {
      let labels = []
      let dataset = []
      let allCount = 0
      let items = []
      if (this.computedExercisesReport) {
        items = [...this.computedExercisesReport.exercises]
        if (items)
          items.forEach(item => {
            labels.push(this.$moment(item.date).format('MM/DD/YYYY'))
            dataset.push(item.progressTime)
            allCount += item.progressTime ? item.progressTime : 0
          })
      }
      return {
        labels: labels,
        datasets: [
          {
            label: 'Exercise minutes',
            data: dataset,
            fill: true,
            borderColor: '#00A0E0',
            backgroundColor: ctx => {
              const canvas = ctx.chart.ctx
              const gradient = canvas.createLinearGradient(0, 0, 0, 160)
              gradient.addColorStop(0, 'rgba(0, 160, 224, 0.2)')
              gradient.addColorStop(1, 'rgba(0, 160, 224, 0)')

              return gradient
            },
            tension: 0.2,
          },
        ],
        allCount: allCount,
      }
    },
    weight() {
      let labels = []
      let dataset = []
      let allCount = 0
      let items = []
      if (this.computedExercisesReport) {
        items = [...this.computedExercisesReport.exercises]
        if (items)
          items.forEach(item => {
            labels.push(this.$moment(item.date).format('MM/DD/YYYY'))
            dataset.push(item.progressWeight)
            allCount += item.progressWeight ? item.progressWeight : 0
          })
      }
      return {
        labels: labels,
        datasets: [
          {
            label: 'Pounds lifted',
            data: dataset,
            fill: true,
            borderColor: '#ED8938',
            backgroundColor: ctx => {
              const canvas = ctx.chart.ctx
              const gradient = canvas.createLinearGradient(0, 0, 0, 160)
              gradient.addColorStop(0, 'rgba(231, 138, 61, 0.2)')
              gradient.addColorStop(1, 'rgba(231, 138, 61, 0)')

              return gradient
            },
            tension: 0.2,
          },
        ],
        allCount: allCount,
      }
    },
    computedDayReport() {
      let ret = {}
      this.exercisesDayReport.forEach(day => {
        let tmpDay = { ...day }
        let additionalExercises = []
        tmpDay.items = tmpDay.items.filter(item => {
          if (item.type == 'SERIES') {
            additionalExercises = [
              ...additionalExercises,
              ...item.series.exercises,
            ]
            return false
          } else return true
        })
        additionalExercises.map(item => {
          item.exercise = {
            id: item.id,
            title: item.title,
            description: item.description,
            time: item.time,
            externalUrl: item.externalUrl,
            progress: item.progress,
            superCompleted: item.superCompleted,
            completed: item.completed,
            done: item.done,
            skipped: item.skipped,
            partially: item.partially,
            media: item.media,
            additionalMedia: item.additionalMedia,
            mobileStaticMedia: item.mobileStaticMedia,
          }
          return { ...item }
        })
        tmpDay.items = [...tmpDay.items, ...additionalExercises]
        ret[day.date] = tmpDay
      })
      return ret
    },
  },
  mounted() {
    this.ready = true
    this.$store.commit('patientsStates/clearExercisesDayReports', {})
    if (this.profileId) this.loadExercisesReport(true)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (vm.ready) vm.loadExercisesReport(true)
    })
  },
  created() {
    this.selectedRange = {
      startDate: this.lastMonth,
      endDate: this.today,
    }
    /******** commented because https://itsorce.atlassian.net/browse/MVP4BH-3367 /********/
    // if (this.$route?.query?.date) {
    //   const endDate = this.$moment(
    //     this.$route?.query?.date,
    //     'YYYY-MM-DD',
    //   ).toDate()
    //   const startDate = this.$moment(this.$route?.query?.date, 'YYYY-MM-DD')
    //     .subtract(30, 'day')
    //     .toDate()
    //   this.selectedRange = {
    //     startDate: startDate,
    //     endDate: endDate,
    //   }
    // } else if (this.$route?.query?.startDate && this.$route?.query?.endDate) {
    //   const startDate = this.$moment(
    //     this.$route.query.startDate,
    //     'YYYY-MM-DD',
    //   ).toDate()
    //   const endDate = this.$moment(
    //     this.$route.query.endDate,
    //     'YYYY-MM-DD',
    //   ).toDate()
    //   this.selectedRange = {
    //     startDate: startDate,
    //     endDate: endDate,
    //   }
    // } else {
    //   this.selectedRange = {
    //     startDate: this.lastMonth,
    //     endDate: this.today,
    //   }
    // }
  },
  watch: {
    profileId(value) {
      if (value) {
        this.loadExercisesReport()
      }
    },
    modeType(value) {
      if (
        (this.modeStatus == 'superCompleted' ||
          this.modeStatus == 'partially') &&
        (value == 'activitiesAM' || value == 'activitiesPM')
      )
        this.modeStatus = 'all'
    },
    selectedRange(newValue, oldValue) {
      if (
        newValue.startDate !== oldValue.startDate ||
        newValue.endDate !== oldValue.endDate
      ) {
        this.$router.replace({
          path: this.$route.path,
          /******** commented because https://itsorce.atlassian.net/browse/MVP4BH-3367 /********/
          // query: {
          //   startDate: this.$moment(newValue.startDate, 'YYYY-MM-DD').format(
          //     'YYYY-MM-DD',
          //   ),
          //   endDate: this.$moment(newValue.endDate, 'YYYY-MM-DD').format(
          //     'YYYY-MM-DD',
          //   ),
          // },
        })
      }
    },
    $route(newRoute, oldRoute) {
      // get new current patient data when patient id changed
      if (
        newRoute.params?.id !== undefined &&
        oldRoute.params?.id !== newRoute.params?.id
      ) {
        this.getPatient(newRoute.params.id)
      }
      /******** commented because https://itsorce.atlassian.net/browse/MVP4BH-3367 /********/
      // if (
      //   newRoute.query?.startDate &&
      //   newRoute.query.startDate !== this.initialStartDate
      // ) {
      //   this.initialStartDate = newRoute.query.startDate
      // }
      // if (
      //   newRoute.query?.endDate &&
      //   newRoute.query.endDate !== this.initialEndDate
      // ) {
      //   this.initialEndDate = newRoute.query.endDate
      // }
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getExercisesReport',
      'getExercisesReportTotals',
      'getExercisesDayReport',
      'getPatient',
    ]),
    ...mapMutations('patientsStates', ['clearExercisesDayReports']),
    loadExercisesReport() {
      if (this.ready) {
        let startDate = this.$moment(this.selectedRange.startDate).format(
          'YYYY-MM-DD',
        )
        let endDate = this.$moment(this.selectedRange.endDate).format(
          'YYYY-MM-DD',
        )
        this.getExercisesReport({
          participantId: this.currentPatient.id,
          params: { startDate: startDate, endDate: endDate },
        }).then(x => {})
        this.getExercisesReportTotals({
          participantId: this.currentPatient.id,
        })
      }
    },
    prepareLoadExercisesReport(range) {
      this.stateDailyRows = true
      this.$nextTick(() => {
        this.stateDailyRows = false
      })
      this.selectedRange.startDate = range.startDate
      this.selectedRange.endDate = range.endDate
      this.loadExercisesReport()
    },
    getDaysBetweenDates(startDate, endDate) {
      let now = startDate.clone(),
        dates = []
      while (now.isSameOrBefore(endDate)) {
        dates.push(now.format('YYYY-MM-DD'))
        now.add(1, 'days')
      }
      return dates
    },
    findDayData(date) {
      let modes = ['superCompleted', 'completed', 'partially', 'skipped']
      let types = ['exercises', 'activitiesAM', 'activitiesPM']
      let charts = [
        'time',
        'progressTime',
        'weight',
        'progressWeight',
        'distance',
        'progressDistance',
      ]
      let vals = {}
      types.forEach(type => {
        let item = this.exercisesReport[type].find(item => item.date == date)
        if (item)
          modes.forEach(mode => {
            if (item[mode] > 0 && !isNaN(item[mode])) {
              if (!vals[mode]) vals[mode] = 0
              vals[mode] += item[mode]
            }
          })
      })
      charts.forEach(chart => {
        let item = this.exercisesReport.exercises.find(
          item => item.date == date,
        )
        if (item) vals[chart] = item[chart]
      })
      let item = this.exercisesReport.steps.find(item => item.date == date)
      if (item) vals.steps = item.steps
      return vals
    },
    loadDayReport(date) {
      this.getExercisesDayReport({
        participantId: this.currentPatient.id,
        date: date,
      }).then(x => {})
    },
    expandAll() {
      if (this.stateDailyRows) {
        this.computedExercisesReport.days.forEach(day => {
          this.loadDayReport(day.date)
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.patient-exercises-report {
  flex: 1;
  padding: 0 10px;
  height: 100%;
  .v-messages {
    display: none;
  }
  h2 {
    color: $bh-black;
  }
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    & > .row {
      margin-bottom: 0;
    }
    & > .row > .col {
      padding-bottom: 0;
    }
    .calendar {
      margin-bottom: 18px;
    }
    .hr {
      border-bottom: 1px solid $bh-gray--5;
      padding-bottom: 10px;
    }
  }
  &__filters {
    &--cards {
      margin-top: 16px;
      margin-bottom: 16px;
      .v-card {
        margin-right: 8px;
        padding: 8px 16px;
        border: 2px solid $bh-white;
        transition-duration: 300ms;
        transition-property: border;
        cursor: pointer;
        overflow: hidden;
        display: flex;
        flex-wrap: wrap;
        width: 246px;
        height: 96px;
        align-content: center;
        &--link:focus::before {
          opacity: 0;
        }
        .bg {
          fill: url('#gradient');
        }
        &:hover {
          border-color: $bh-blue--4;
        }
        &.active {
          border-color: $bh-blue;
        }
        .v-icon {
          height: 30px !important;
          line-height: 30px !important;
        }
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 4px;
          display: inline-block;
          margin-right: 8px;
        }
        h3 {
          color: $bh-black;
        }
        p {
          font-size: 12px;
          margin-bottom: 0;
          width: 100%;
        }
        &.completed {
          .v-icon {
            color: #00a0e0;
            .bg {
              fill: url('#gradient-completed');
            }
          }
          .dot {
            background: #00a0e0;
          }
        }
        &.super-completed {
          .v-icon {
            color: #945eff;
            .bg {
              fill: url('#gradient-super-completed');
            }
          }
          .dot {
            background: #945eff;
          }
        }
        &.skipped {
          .v-icon {
            color: #ff6760;
            .bg {
              fill: url('#gradient-skipped');
            }
          }
          .dot {
            background: #ff6760;
          }
        }
        &.partially {
          .v-icon {
            color: #bdd0ff;
            .bg {
              fill: url('#gradient-partially');
            }
          }
          .dot {
            background: #bdd0ff;
          }
        }
      }
    }
  }
  &__metrics {
    h3,
    h5 {
      color: $bh-black;
    }
  }
  .metrics-expansion-panel {
    border-radius: 0;

    .v-expansion-panel {
      &-header {
        padding: 0 !important;
      }
      &:before {
        display: none !important;
      }
    }
  }
}
</style>
